import { useCallback, useEffect, useRef } from 'react';
import { Map } from 'mapbox-gl';
import { useDispatch } from 'react-redux';
import { polygon } from '@turf/helpers';

import { makeEmptyGeojson } from 'modules/geojson';
import { InteractionLayersSources } from 'modules/mapLayers';
import { hideAllUi, showAllUi } from 'modules/metadata';

import { MapEvent } from '../types';

const rectangleRing = (
  corner: GeoJSON.Position,
  opposite: GeoJSON.Position,
): GeoJSON.Position[] => [
  [corner[0], corner[1]],
  [opposite[0], corner[1]],
  [opposite[0], opposite[1]],
  [corner[0], opposite[1]],
  [corner[0], corner[1]],
];

export const useCreateRectangleOnMap = (
  map: Map,
  onChange: (ring: GeoJSON.Position[] | null) => void,
): { clean: () => void } => {
  const dispatch = useDispatch();
  // Top-right corner, captured on the first click.
  const firstCorner = useRef<GeoJSON.Position | null>(null);
  // Once both corners are placed the rectangle is locked: further clicks are
  // ignored until `clean` resets the state.
  const isComplete = useRef(false);
  // Keep the latest callback without re-subscribing the map listeners.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const updateMap = useCallback(
    (ring: GeoJSON.Position[] | null, creating = false) => {
      const geojson = makeEmptyGeojson();
      if (ring) {
        geojson.features = [polygon([ring], { creating })];
      }
      map
        .getSource(InteractionLayersSources.RectangleCreation)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        .setData(geojson);
    },
    [map],
  );

  const handleMouseMove = useCallback(
    (ev: MapEvent): void => {
      if (!firstCorner.current) return;
      const ring = rectangleRing(firstCorner.current, [
        ev.lngLat.lng,
        ev.lngLat.lat,
      ]);
      updateMap(ring, true);
      onChangeRef.current(ring);
    },
    [updateMap],
  );

  const handleClick = useCallback(
    (ev: MapEvent): void => {
      if (isComplete.current) return;
      if (!firstCorner.current) {
        firstCorner.current = [ev.lngLat.lng, ev.lngLat.lat];
        map.on('mousemove', handleMouseMove);
        return;
      }
      const ring = rectangleRing(firstCorner.current, [
        ev.lngLat.lng,
        ev.lngLat.lat,
      ]);
      updateMap(ring);
      onChangeRef.current(ring);
      firstCorner.current = null;
      map.off('mousemove', handleMouseMove);
      // Lock drawing once the rectangle is committed.
      isComplete.current = true;
      map.getCanvas().style.cursor = '';
    },
    [map, updateMap, handleMouseMove],
  );

  // Erase both points and re-enable drawing a new rectangle.
  const clean = useCallback((): void => {
    firstCorner.current = null;
    isComplete.current = false;
    map.off('mousemove', handleMouseMove);
    map.getCanvas().style.cursor = 'crosshair';
    updateMap(null);
    onChangeRef.current(null);
  }, [map, handleMouseMove, updateMap]);

  useEffect(() => {
    dispatch(hideAllUi());
    map.getCanvas().style.cursor = 'crosshair';
    map.on('click', handleClick);

    return (): void => {
      dispatch(showAllUi());
      map.getCanvas().style.cursor = '';
      map.off('click', handleClick);
      map.off('mousemove', handleMouseMove);
      updateMap(null);
    };
  }, [map, dispatch, handleClick, handleMouseMove, updateMap]);

  return { clean };
};
